import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewChild
} from '@angular/core';
import SignaturePad from 'signature_pad';
import { CommonModule } from '@angular/common';

export interface SignaturePadOptions {
  dotSize?: number | ((velocity: number) => number);
  minWidth?: number;
  maxWidth?: number;
  throttle?: number;
  minDistance?: number;
  backgroundColor?: string;
  penColor?: string;
  velocityFilterWeight?: number;
}

@Component({
  selector: 'lib-signature-pad',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './signature-pad.component.html',
  styleUrl: './signature-pad.component.scss'
})
export class SignaturePadComponent implements AfterViewInit {
  @ViewChild('canvas', { static: false }) canvasRef!: ElementRef<HTMLCanvasElement>;

  // Inputs
  options = input<SignaturePadOptions>({});
  width = input<string>('100%');
  height = input<string>('400px');
  backgroundColor = input<string>('rgb(255,255,255,0)');

  // Outputs
  signatureSaved = output<string>(); // Emits data URL
  signatureCleared = output<void>();

  #destroyRef = inject(DestroyRef);
  signaturePad: SignaturePad | null = null;
  isEmpty = signal<boolean>(true);

  ngAfterViewInit(): void {
    this.initializeSignaturePad();
    this.setupResizeHandler();
  }

  private initializeSignaturePad(): void {
    if (!this.canvasRef?.nativeElement) {
      return;
    }

    const canvas = this.canvasRef.nativeElement;
    const options = this.options();

    // Set canvas size
    this.resizeCanvas();

    // Initialize SignaturePad with options
    const padOptions: any = {
      penColor: options.penColor || 'black',
      minWidth: options.minWidth || 0.5,
      maxWidth: options.maxWidth || 2.5,
      throttle: options.throttle || 16,
      minDistance: options.minDistance || 5,
      velocityFilterWeight: options.velocityFilterWeight || 0.7
    };

    // Handle dotSize which can be number or function
    if (options.dotSize !== undefined) {
      padOptions.dotSize = options.dotSize;
    } else {
      padOptions.dotSize = 0;
    }

    this.signaturePad = new SignaturePad(canvas, padOptions);

    // Add event listeners
    this.signaturePad.addEventListener('beginStroke', () => {
      this.isEmpty.set(false);
    });

    this.signaturePad.addEventListener('endStroke', () => {
      this.isEmpty.set(this.signaturePad?.isEmpty() ?? true);
    });
  }

  private resizeCanvas(): void {
    if (!this.canvasRef?.nativeElement) {
      return;
    }

    const canvas = this.canvasRef.nativeElement;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);

    // Get the container dimensions
    const container = canvas.parentElement;
    if (container) {
      canvas.width = container.clientWidth * ratio;
      canvas.height = container.clientHeight * ratio;
      canvas.getContext('2d')?.scale(ratio, ratio);
    }
  }

  private setupResizeHandler(): void {
    const handleResize = () => {
      if (this.signaturePad && !this.signaturePad.isEmpty()) {
        const data = this.signaturePad.toData();
        this.resizeCanvas();
        this.signaturePad.fromData(data);
      } else {
        this.resizeCanvas();
      }
    };

    window.addEventListener('resize', handleResize);

    window.addEventListener('orientationchange', () => {
      setTimeout(handleResize, 100);
    });
  }

  clear(): void {
    this.signaturePad?.clear();
    this.isEmpty.set(true);
    this.signatureCleared.emit();
  }

  // Fixed export dimensions
  exportWidth = input<number>(500);
  exportHeight = input<number>(300);

  save(format: 'image/png' | 'image/jpeg' | 'image/svg+xml' = 'image/png', quality?: number): string {
    if (!this.signaturePad || !this.canvasRef?.nativeElement) {
      return '';
    }

    let dataUrl = '';
    if (format === 'image/svg+xml') {
      dataUrl = this.signaturePad.toSVG();
    } else {
      // Create a temporary canvas with fixed export dimensions
      const exportCanvas = document.createElement('canvas');
      const targetWidth = this.exportWidth();
      const targetHeight = this.exportHeight();

      exportCanvas.width = targetWidth;
      exportCanvas.height = targetHeight;

      const ctx = exportCanvas.getContext('2d');
      if (ctx) {
        // Fill with background color
        ctx.fillStyle = this.backgroundColor();
        ctx.fillRect(0, 0, targetWidth, targetHeight);

        // Draw the original canvas scaled to fit the export dimensions
        const sourceCanvas = this.canvasRef.nativeElement;
        ctx.drawImage(sourceCanvas, 0, 0, sourceCanvas.width, sourceCanvas.height, 0, 0, targetWidth, targetHeight);

        dataUrl = exportCanvas.toDataURL(format, quality);
      } else {
        // Fallback to original method if context creation fails
        dataUrl = this.signaturePad.toDataURL(format, quality);
      }
    }

    this.signatureSaved.emit(dataUrl);
    return dataUrl;
  }

  isEmptySignature(): boolean {
    return this.signaturePad?.isEmpty() ?? true;
  }

  fromDataURL(dataUrl: string): void {
    if (this.signaturePad) {
      this.signaturePad.fromDataURL(dataUrl);
      this.isEmpty.set(this.signaturePad.isEmpty());
    }
  }

  fromData(data: any[]): void {
    if (this.signaturePad) {
      this.signaturePad.fromData(data);
      this.isEmpty.set(this.signaturePad.isEmpty());
    }
  }

  toData(): any[] {
    return this.signaturePad?.toData() ?? [];
  }

  setPenColor(color: string): void {
    if (this.signaturePad) {
      this.signaturePad.penColor = color;
    }
  }

  setMinWidth(width: number): void {
    if (this.signaturePad) {
      this.signaturePad.minWidth = width;
    }
  }

  setMaxWidth(width: number): void {
    if (this.signaturePad) {
      this.signaturePad.maxWidth = width;
    }
  }
}
